/**
 * The interface kit: small functions that build DOM in the game's language (stone, bronze, ember).
 * No framework: each returns its element (and, where it has state, a handle to change it). Styles
 * live in ui.css; nothing here animates anything but transform and opacity.
 */

type Child = Node | string | null | undefined | false;
type Attrs = Record<string, unknown>;

/**
 * `h('div', { class: 'x', role: 'dialog', onclick: fn }, child…)`: `class`, `data-*`, `aria-*` and
 * `role` become attributes; anything else is set as a property (type, disabled, onclick, …).
 */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = String(v);
    else if (k === 'role' || k.startsWith('data-') || k.startsWith('aria-')) el.setAttribute(k, v === true ? '' : String(v));
    else (el as unknown as Record<string, unknown>)[k] = v;
  }
  for (const c of children) if (c != null && c !== false) el.append(c);
  return el;
}

const SVG = 'http://www.w3.org/2000/svg';
/** A small stroked icon (24×24 view box) from path data. */
export function icon(paths: string[], stroke = 2): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', String(stroke));
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
  for (const d of paths) { const p = document.createElementNS(SVG, 'path'); p.setAttribute('d', d); svg.append(p); }
  return svg;
}
export const ICONS = {
  back: ['M15 5 8 12l7 7'],
  menu: ['M5 7h14', 'M5 12h14', 'M5 17h14'],
  // a cog (after Feather's «settings», MIT)
  gear: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z'],
  close: ['M6 6l12 12', 'M18 6 6 18'],
};

/* ---------- text ---------- */

/** A small-caps label; `rule` adds the fading bronze line after it (section headings). */
export const label = (text: string, rule = false) => h('div', { class: rule ? 'ui-label ui-rule' : 'ui-label' }, text);
export const kbd = (key: string) => h('span', { class: 'ui-kbd', 'aria-hidden': 'true' }, key);

/* ---------- panels and buttons ---------- */

export type Tone = 'stone' | 'teal' | 'ember' | 'well';
/** A bronze-edged stone panel with chamfered corners. */
export function panel(o: { tone?: Tone; cut?: number; class?: string } = {}, ...children: Child[]): HTMLDivElement {
  const el = h('div', { class: ['ui-panel', o.tone && o.tone !== 'stone' ? o.tone : '', o.class ?? ''].filter(Boolean).join(' ') }, ...children);
  if (o.cut) el.style.setProperty('--cut', `${o.cut}px`);
  return el;
}

export type Variant = 'bronze' | 'ember' | 'teal' | 'ghost' | 'danger';
export interface ButtonOptions { variant?: Variant; size?: 'sm' | 'md' | 'lg'; key?: string; block?: boolean; type?: 'button' | 'submit'; title?: string; onClick?: (e: MouseEvent) => void }
/** A chamfered button: `ember` is the one main action of a screen, `bronze` the rest. */
export function button(text: string | Node, o: ButtonOptions = {}): HTMLButtonElement & { face: HTMLSpanElement } {
  const face = h('span', { class: 'ui-btn-face' }, text, o.key ? kbd(o.key) : null);
  const cls = ['ui-btn', o.variant ?? 'bronze', o.size && o.size !== 'md' ? o.size : '', o.block ? 'ui-btn-block' : ''].filter(Boolean).join(' ');
  const b = h('button', { class: cls, type: o.type ?? 'button', title: o.title }, face) as HTMLButtonElement & { face: HTMLSpanElement };
  b.face = face;
  if (o.onClick) b.addEventListener('click', o.onClick);
  return b;
}
/** Shows a spinner in place of the label and blocks the button while `work` runs. */
export async function busy<T>(b: HTMLButtonElement, work: () => Promise<T>): Promise<T> {
  b.disabled = true; b.setAttribute('aria-busy', 'true');
  try { return await work(); } finally { b.disabled = false; b.removeAttribute('aria-busy'); }
}

/** An octagonal bronze medallion button with an icon (back, settings, menu). */
export function octButton(paths: string[], ariaLabel: string, o: { size?: number; onClick?: () => void } = {}): HTMLButtonElement {
  const b = h('button', { class: 'ui-oct', type: 'button', 'aria-label': ariaLabel, title: ariaLabel }, h('i', null, icon(paths)));
  // a finger needs 44 px whatever the design's size
  if (o.size) b.style.setProperty('--s', `${COARSE ? Math.max(44, o.size) : o.size}px`);
  if (o.onClick) b.addEventListener('click', o.onClick);
  return b;
}
const COARSE = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** The nick's first letter in a bronze octagon (`empty`: a seat still waiting for someone). */
export function medallion(nick: string | null, o: { size?: number; tone?: 'teal' | 'empty' } = {}): HTMLDivElement {
  const el = h('div', { class: `ui-medal${o.tone ? ' ' + o.tone : ''}`, 'aria-hidden': 'true' }, h('b', null, firstLetter(nick)));
  if (o.size) el.style.setProperty('--s', `${o.size}px`);
  return el;
}
/** The first letter (a whole grapheme where the platform can tell), upper-case. */
export function firstLetter(nick: string | null): string {
  const s = (nick ?? '').replace(/^[\s_-]+/, '');
  if (!s) return '';
  const Seg = (Intl as unknown as { Segmenter?: new (l: string, o: object) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  const first = Seg ? [...new Seg('uk', { granularity: 'grapheme' }).segment(s)][0].segment : [...s][0];
  return first.toLocaleUpperCase('uk');
}

/* ---------- status ---------- */

export type StatusTone = 'ok' | 'bad' | 'wait' | '';
/** A dot and a few words, announced politely to screen readers. */
export function status(): { el: HTMLDivElement; set(text: string, tone?: StatusTone): void } {
  const el = h('div', { class: 'ui-status', 'aria-live': 'polite' });
  return { el, set(text, tone = '') { el.textContent = text; if (tone) el.dataset.tone = tone; else delete el.dataset.tone; } };
}

/* ---------- controls ---------- */

export interface Segmented<T extends string> { el: HTMLDivElement; value: T; set(v: T): void }
/** Tabs or a choice of a few (quality): one checked, arrows move between them. */
export function segmented<T extends string>(options: readonly { value: T; label: string }[], value: T, onChange: (v: T) => void, ariaLabel = ''): Segmented<T> {
  const el = h('div', { class: 'ui-seg', role: 'radiogroup', 'aria-label': ariaLabel || null });
  const buttons = options.map((o) => h('button', { type: 'button', role: 'radio', 'data-value': o.value }, o.label));
  const api: Segmented<T> = {
    el, value,
    set(v) {
      api.value = v;
      buttons.forEach((b, i) => { const on = options[i].value === v; b.setAttribute('aria-checked', String(on)); b.tabIndex = on ? 0 : -1; });
    },
  };
  const pick = (i: number, focus = false) => {
    const v = options[i].value;
    if (focus) buttons[i].focus();
    if (v === api.value) return;
    api.set(v); onChange(v);
  };
  buttons.forEach((b, i) => {
    b.addEventListener('click', () => pick(i));
    b.addEventListener('keydown', (e) => {
      const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!d) return;
      e.preventDefault();
      pick((i + d + options.length) % options.length, true);
    });
  });
  el.append(...buttons);
  api.set(value);
  return api;
}

/** An on/off switch (role="switch"). */
export function toggle(checked: boolean, onChange: (on: boolean) => void, ariaLabel: string): HTMLButtonElement & { checked: boolean } {
  const b = h('button', { class: 'ui-toggle', type: 'button', role: 'switch', 'aria-label': ariaLabel }) as HTMLButtonElement & { checked: boolean };
  Object.defineProperty(b, 'checked', {
    get: () => b.getAttribute('aria-checked') === 'true',
    set: (v: boolean) => b.setAttribute('aria-checked', String(v)),
  });
  b.checked = checked;
  b.addEventListener('click', () => { b.checked = !b.checked; onChange(b.checked); });
  return b;
}

/** A settings row: title and a line under it on the left, a control or a value on the right. */
export const row = (title: string, sub: string | null, end: Child) =>
  h('div', { class: 'ui-row' }, h('div', { class: 'ui-row-text' }, h('div', { class: 'ui-row-title' }, title), sub ? h('div', { class: 'ui-row-sub' }, sub) : null), end);

export interface CodeCells {
  el: HTMLDivElement; input: HTMLInputElement; readonly value: string;
  set(v: string): void; clear(): void; focus(): void; tone(t: 'ok' | 'bad' | ''): void;
}
/**
 * A code typed into bronze wells (room code, device code). One real input lies over the cells, so
 * paste, the phone's keyboard and autofill work as usual. `normalize` turns whatever was typed or
 * pasted into the code's own characters (a pasted invite link becomes its code); `groups` splits the
 * wells visually (8 → 4 + 4).
 */
export function codeCells(o: { length: number; normalize: (raw: string) => string; groups?: number[]; ariaLabel: string; onInput?: (v: string) => void; onComplete?: (v: string) => void }): CodeCells {
  const input = h('input', { type: 'text', autocomplete: 'one-time-code', spellcheck: false, autocapitalize: 'characters', enterKeyHint: 'go', 'aria-label': o.ariaLabel });
  const cells = Array.from({ length: o.length }, () => h('div', { class: 'ui-cell' }, h('b')));
  const el = h('div', { class: 'ui-code' });
  const groups = o.groups ?? [o.length];
  let at = 0;
  for (const [g, n] of groups.entries()) { if (g) el.append(h('div', { class: 'ui-code-gap' })); el.append(...cells.slice(at, at + n)); at += n; }
  el.append(input);
  let value = '', completed = '';
  const render = () => {
    cells.forEach((c, i) => {
      const ch = value[i] ?? '';
      const b = c.firstElementChild as HTMLElement;
      if (b.textContent !== ch) { b.textContent = ch; if (ch) { c.removeAttribute('data-filled'); void c.offsetWidth; c.setAttribute('data-filled', ''); } else c.removeAttribute('data-filled'); }
      c.toggleAttribute('data-caret', i === Math.min(value.length, o.length - 1) && (i === value.length || value.length === o.length));
    });
  };
  const update = (raw: string) => {
    const v = o.normalize(raw).slice(0, o.length);
    input.value = v;
    if (v === value) return;
    value = v; delete el.dataset.tone;
    render(); o.onInput?.(v);
    if (v.length === o.length && v !== completed) { completed = v; o.onComplete?.(v); }
    if (v.length < o.length) completed = '';
  };
  input.addEventListener('input', () => update(input.value));
  // the caret always stays at the end: the wells show a code, not an editable text
  input.addEventListener('focus', () => { const n = input.value.length; input.setSelectionRange?.(n, n); });
  input.addEventListener('keydown', (e) => { e.stopPropagation(); });
  render();
  return {
    el, input,
    get value() { return value; },
    set: (v) => update(v),
    clear: () => update(''),
    focus: () => input.focus(),
    tone: (t) => { if (t) el.dataset.tone = t; else delete el.dataset.tone; },
  };
}

/** A code shown, not typed: the same wells, read-only (a room code, a device code). */
export function codeView(code: string, groups: number[] = [code.length], ariaLabel = 'Код'): HTMLDivElement {
  const el = h('div', { class: 'ui-code ui-code-view', role: 'img', 'aria-label': `${ariaLabel}: ${[...code].join(' ')}` });
  let at = 0;
  for (const [g, n] of groups.entries()) {
    if (g) el.append(h('div', { class: 'ui-code-gap' }));
    for (const ch of code.slice(at, at + n)) { el.append(h('div', { class: 'ui-cell', 'data-filled': '', style: `--i:${at}` }, h('b', null, ch))); at++; }
  }
  return el;
}

/** Page dots; the current one stretches. */
export function pager(n: number): { el: HTMLDivElement; set(i: number): void } {
  const dots = Array.from({ length: n }, () => h('i'));
  const el = h('div', { class: 'ui-pager', 'aria-hidden': 'true' }, ...dots);
  return { el, set(i) { dots.forEach((d, k) => d.toggleAttribute('data-on', k === i)); } };
}

/** A short shake (a refused nick, a wrong code). Restarts if called again. */
export function shake(el: HTMLElement) {
  el.classList.remove('ui-shake'); void el.offsetWidth; el.classList.add('ui-shake');
  el.addEventListener('animationend', () => el.classList.remove('ui-shake'), { once: true });
}
